import { useEffect, useMemo, useState, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { APP_VERSION, CHANGELOG } from "../changelog";
import {
  computeWhatsNewState,
  computeNewVersions,
  isWhatsNewEnabledForLevel,
} from "../utils/whatsNew";

const SEEN_KEY = "conext_changelog_seen_ventas_v1";

function readSeen() {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch (_) {
    return null;
  }
}

function writeSeen(version) {
  try {
    localStorage.setItem(SEEN_KEY, version);
  } catch (_) {}
}

// Nivel del usuario de la sesión, leído del JWT igual que `RequireNotFieldVendor`.
// Sin token o token ilegible → null (el gate hace fail-open).
function readLevel() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const p = jwtDecode(token);
    return p?.level ?? p?.dept_level ?? p?.admin_level ?? null;
  } catch (_) {
    return null;
  }
}

export function useWhatsNew() {
  const [open, setOpen] = useState(false);
  const [hasUnseen, setHasUnseen] = useState(false);
  // Versión vista capturada al montar; no cambia hasta recargar la app.
  const [stored] = useState(readSeen);
  // Gate por rol, capturado al montar (el nivel no cambia dentro de una sesión).
  const [enabled] = useState(() => isWhatsNewEnabledForLevel(readLevel()));

  const newVersions = useMemo(
    () => new Set(computeNewVersions(stored, CHANGELOG)),
    [stored],
  );

  useEffect(() => {
    if (!enabled) return;
    const { shouldOpen } = computeWhatsNewState(stored, APP_VERSION);
    if (shouldOpen) {
      setHasUnseen(true);
      setOpen(true);
    }
  }, [stored, enabled]);

  const openModal = useCallback(() => {
    if (!enabled) return;
    setOpen(true);
  }, [enabled]);

  const closeModal = useCallback(() => {
    setOpen(false);
    writeSeen(APP_VERSION);
    setHasUnseen(false);
  }, []);

  return {
    open: enabled && open,
    openModal,
    closeModal,
    hasUnseen: enabled && hasUnseen,
    version: APP_VERSION,
    newVersions,
    enabled,
  };
}
